import React, { useEffect, useMemo, useRef } from "react";
import { Box, Typography, GlobalStyles } from "@mui/material";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { TILES, MAP_ATTRIBUTION, tooltipCss } from "./mapShared";
import { DRC_CENTER } from "../../config/drcAdmin";

/* ═══════════════════════════════════════════════════════════════════
   COLOR TOKENS
   Passed down from DashboardPage's `C` palette via the `colors` prop so
   the map can never drift from the page theme. DEFAULT_COLORS mirrors
   those tokens for standalone use.
══════════════════════════════════════════════════════════════════ */
export type MapColors = {
  tooltipBg: string;
  tooltipText: string;
  tooltipTextMuted: string;
};

// Mirrors DashboardPage.tsx tokens: C.headerBg, C.onDark, C.onDarkMuted.
const DEFAULT_COLORS: MapColors = {
  tooltipBg: "#0f1f35",
  tooltipText: "rgba(255,255,255,0.90)",
  tooltipTextMuted: "rgba(255,255,255,0.45)",
};

const F_SANS = "'Inter', 'Helvetica Neue', sans-serif";
// Must stay in sync with DashboardPage.tsx S.lg
const SHADOW_LG = "0 10px 36px rgba(15,31,53,0.13)";

/* ═══════════════════════════════════════════════════════════════════
   UTILITIES
══════════════════════════════════════════════════════════════════ */

// Common French spellings/variants/typos for the countries below, keyed by their
// accent-stripped lowercase form → the COUNTRY_ISO key they resolve to. Only
// relevant for full-name input (see resolveCountryCode) — real data is almost
// always a bare ISO2/ISO3 code (e.g. country_of_origin = "BEL"), matched
// directly with no name table at all.
const COUNTRY_ALIASES: Record<string, string> = {
  "rdc": "democratic republic of the congo",
  "r.d.c": "democratic republic of the congo",
  "rd congo": "democratic republic of the congo",
  "congo kinshasa": "democratic republic of the congo",
  "congo-kinshasa": "democratic republic of the congo",
  "congo (kinshasa)": "democratic republic of the congo",
  "rca": "central african republic",
  "centrafrique": "central african republic",
  "republique centrafricaine": "central african republic",
  "congo brazzaville": "congo",
  "congo-brazzaville": "congo",
  "congo (brazzaville)": "congo",
  "republique du congo": "congo",
  "ouganda": "uganda",
  "afrique du sud": "south africa",
  "soudan du sud": "south sudan",
  "soudan": "sudan",
  "zambie": "zambia",
  "tanzanie": "tanzania",
  "ethiopie": "ethiopia",
  "belgique": "belgium",
  "chine": "china",
  "inde": "india",
  "suisse": "switzerland",
  "allemagne": "germany",
  "emirats arabes unis": "united arab emirates",
  "arabie saoudite": "saudi arabia",
  "arabie": "saudi arabia",
};

const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");
const stripAccents = (s: string) => s.normalize("NFD").replace(COMBINING_MARKS, "");

// Accent-stripped, alias-resolved, trimmed key — the single source of truth
// used for both the coordinate lookup and the ISO lookup below, so a country
// alias is only ever defined once and applies everywhere.
const normalizeCountryKey = (raw: string): string => {
  const cleaned = stripAccents((raw || "").toLowerCase().trim()).replace(/\s+/g, " ");
  return COUNTRY_ALIASES[cleaned] || cleaned;
};

// ISO 3166-1 alpha-2 codes for the countries above — used to match choropleth
// polygon features (which key by ISO code) to our screening counts.
const COUNTRY_ISO: Record<string, string> = {
  "guinea": "GN", "sierra leone": "SL", "liberia": "LR",
  "democratic republic of the congo": "CD", "uganda": "UG", "kenya": "KE",
  "nigeria": "NG", "cameroon": "CM", "south sudan": "SS", "ethiopia": "ET",
  "malawi": "MW", "mozambique": "MZ", "tanzania": "TZ", "zambia": "ZM",
  "zimbabwe": "ZW", "south africa": "ZA", "angola": "AO", "congo": "CG",
  "gabon": "GA", "central african republic": "CF", "chad": "TD", "sudan": "SD",
  "egypt": "EG", "ghana": "GH", "ivory coast": "CI", "benin": "BJ",
  "togo": "TG", "mali": "ML", "burkina faso": "BF", "niger": "NE",
  "senegal": "SN", "burundi": "BI", "rwanda": "RW", "somalia": "SO",
  "france": "FR", "united states": "US", "united kingdom": "GB", "canada": "CA",
  "belgium": "BE", "netherlands": "NL", "germany": "DE", "italy": "IT",
  "spain": "ES", "switzerland": "CH", "australia": "AU", "india": "IN",
  "china": "CN", "japan": "JP", "thailand": "TH", "singapore": "SG",
  "united arab emirates": "AE", "saudi arabia": "SA", "lebanon": "LB", "pakistan": "PK",
};

const ALPHA3_RE = /^[A-Za-z]{3}$/;
const ALPHA2_RE = /^[A-Za-z]{2}$/;

// Resolves any incoming `country` value (from traveler_screenings.country_of_origin)
// to a single ISO code string. Real data is a bare ISO2/ISO3 code — matched
// directly, no table lookup at all. A full name (French or English) falls back
// to the alias + ISO tables above, with fuzzy substring match as a last resort.
const resolveCountryCode = (raw: string): string | null => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;
  if (ALPHA3_RE.test(trimmed) || ALPHA2_RE.test(trimmed)) return trimmed.toUpperCase();
  const key = normalizeCountryKey(trimmed);
  if (COUNTRY_ISO[key]) return COUNTRY_ISO[key];
  const fuzzyKey = Object.keys(COUNTRY_ISO).find((k) => key.includes(k) || k.includes(key));
  return fuzzyKey ? COUNTRY_ISO[fuzzyKey] : null;
};

type Hotspot = {
  code: string;
  count: number;
  highRisk: number;
};

/* ═══════════════════════════════════════════════════════════════════
   CHOROPLETH — quantile shading by traveler volume, ported from the
   reference report HTML's origin-country map.
══════════════════════════════════════════════════════════════════ */
const SHADE_BINS = [
  { label: "Low",       quantile: "0-20%",   color: "#ffd166" },
  { label: "Medium",    quantile: "21-40%",  color: "#fdbb84" },
  { label: "High",      quantile: "41-60%",  color: "#fc8d59" },
  { label: "Very High", quantile: "61-80%",  color: "#d94801" },
  { label: "Highest",   quantile: "81-100%", color: "#7f2704" },
];

type ShadeBin = { label: string; quantile: string; color: string; min: number; max: number };

const buildQuantileScale = (values: number[]): ShadeBin[] => {
  const counts = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (!counts.length) return [];
  const thresholds = SHADE_BINS.map((_, i) => {
    const rank = Math.ceil(((i + 1) / SHADE_BINS.length) * counts.length) - 1;
    return counts[Math.max(0, Math.min(rank, counts.length - 1))];
  });
  const scale: ShadeBin[] = [];
  let min = 0;
  thresholds.forEach((max, i) => {
    if (max < min) return;
    scale.push({ ...SHADE_BINS[i], min, max });
    min = max + 1;
  });
  return scale;
};

const getShade = (value: number, scale: ShadeBin[]): string =>
  (scale.find((b) => value >= b.min && value <= b.max) || scale[scale.length - 1] || SHADE_BINS[0]).color;

// Returns BOTH the alpha-3 and alpha-2 codes for a feature (whichever exist) —
// real country_of_origin data is alpha-3, so isoCounts is keyed by alpha-3 for
// the common case, but a feature match should still succeed via alpha-2 for
// the full-name fallback path (resolveCountryCode only ever returns ISO2 there).
const getFeatureIsoCodes = (props: Record<string, any>): string[] => {
  const iso3 = props["ISO3166-1-Alpha-3"] || props.ISO_A3 || props.ADM0_A3;
  const iso2 = props["ISO3166-1-Alpha-2"] || props.ISO_A2 || props.iso_a2;
  return [iso3, iso2].filter(Boolean).map((c) => String(c).toUpperCase());
};

const lookupIso = (map: Record<string, number>, codes: string[]): number =>
  codes.reduce((acc, c) => acc || map[c] || 0, 0);

const WORLD_GEOJSON_URL = "https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson";
// Module-level cache: fetch the ~250KB world-borders GeoJSON once per browser
// session, not on every 30s dashboard refresh or component remount.
let worldGeoJsonCache: Promise<any> | null = null;
const fetchWorldGeoJson = () => {
  if (!worldGeoJsonCache) worldGeoJsonCache = fetch(WORLD_GEOJSON_URL).then((r) => r.json());
  return worldGeoJsonCache;
};

const numFmt = (v: number | undefined | null) => (v ?? 0).toLocaleString();

// All the data available for a country, not just its total — total screened,
// high-risk count, and the cleared count implied by the difference.
const buildTooltipHtml = (name: string, value: number, highRisk: number): string => {
  if (!value) return `<b>${name}</b><br>No data`;
  const cleared = Math.max(0, value - highRisk);
  return (
    `<b>${name}</b><br>` +
    `${numFmt(value)} screened<br>` +
    `${numFmt(cleared)} cleared &middot; ${numFmt(highRisk)} high-risk`
  );
};

const ChoroplethLayer: React.FC<{ isoCounts: Record<string, number>; isoHighRisk: Record<string, number>; scale: ShadeBin[] }> = ({ isoCounts, isoHighRisk, scale }) => {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);
  const dataRef = useRef({ isoCounts, isoHighRisk, scale });
  dataRef.current = { isoCounts, isoHighRisk, scale };

  useEffect(() => {
    let cancelled = false;
    fetchWorldGeoJson()
      .then((geo) => {
        if (cancelled) return;
        const layer = L.geoJSON(geo, {
          style: (feature: any) => {
            const codes = getFeatureIsoCodes(feature.properties || {});
            const value = lookupIso(dataRef.current.isoCounts, codes);
            return {
              fillColor: getShade(value, dataRef.current.scale),
              fillOpacity: value ? 0.75 : 0.06,
              color: "#223344", weight: 0.6, opacity: 0.7,
            };
          },
          onEachFeature: (feature: any, lyr: L.Layer) => {
            const props = feature.properties || {};
            const codes = getFeatureIsoCodes(props);
            const value = lookupIso(dataRef.current.isoCounts, codes);
            const highRisk = lookupIso(dataRef.current.isoHighRisk, codes);
            const name = props.name || props.ADMIN || props.NAME || codes[0] || "Unknown";
            // Tooltip (not Popup) — opens automatically on hover, no click needed
            (lyr as any).bindTooltip(
              buildTooltipHtml(name, value, highRisk),
              { className: "geo-hover-tip", sticky: true, direction: "top" }
            );
          },
        });
        layer.addTo(map);
        layerRef.current = layer;
      })
      .catch(() => {
        // Graceful degradation — tiles-only if the GeoJSON is unreachable
      });
    return () => {
      cancelled = true;
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Re-style in place (not re-fetch/rebuild) whenever counts/scale change
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.eachLayer((lyr: any) => {
      const props = lyr.feature?.properties || {};
      const codes = getFeatureIsoCodes(props);
      const value = lookupIso(isoCounts, codes);
      const highRisk = lookupIso(isoHighRisk, codes);
      lyr.setStyle({ fillColor: getShade(value, scale), fillOpacity: value ? 0.75 : 0.06 });
      const name = props.name || props.ADMIN || props.NAME || codes[0] || "Unknown";
      lyr.setTooltipContent(buildTooltipHtml(name, value, highRisk));
    });
  }, [isoCounts, isoHighRisk, scale]);

  return null;
};

type MapPoint = {
  country?: string;
  risk?: string;
  count?: number;
  highRisk?: number;
};

type Props = {
  points?: MapPoint[];
  colors?: Partial<MapColors>;
  mode?: "light" | "dark";
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const GeoDashboardMap: React.FC<Props> = ({ points = [], colors: colorsProp, mode = "light" }) => {
  const colors = useMemo<MapColors>(() => ({ ...DEFAULT_COLORS, ...colorsProp }), [colorsProp]);

  const hotspots = useMemo<Hotspot[]>(() => {
    return points
      .map((point) => {
        if (!point.country) return null;
        const code = resolveCountryCode(point.country);
        if (!code) return null;
        const count = point.count && point.count > 0 ? point.count : 1;
        return { code, count, highRisk: point.highRisk || 0 };
      })
      .filter((item): item is Hotspot => Boolean(item));
  }, [points]);

  const isoCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    hotspots.forEach((h) => { counts[h.code] = (counts[h.code] || 0) + h.count; });
    return counts;
  }, [hotspots]);

  const isoHighRisk = useMemo(() => {
    const counts: Record<string, number> = {};
    hotspots.forEach((h) => { counts[h.code] = (counts[h.code] || 0) + h.highRisk; });
    return counts;
  }, [hotspots]);

  const shadeScale = useMemo(() => buildQuantileScale(Object.values(isoCounts)), [isoCounts]);

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", borderRadius: "16px", overflow: "hidden" }}>
      <GlobalStyles styles={tooltipCss(colors.tooltipBg, SHADOW_LG)} />
      <MapContainer center={DRC_CENTER} zoom={2} minZoom={1} scrollWheelZoom={false} style={{ width: "100%", height: "100%" }}>
        {/* key={mode} forces a clean tile-layer remount on theme switch; MapContainer (viewport, animations) persists */}
        <TileLayer key={mode} url={TILES[mode].url} attribution={MAP_ATTRIBUTION} subdomains={TILES[mode].subdomains} maxZoom={19} />
        <ChoroplethLayer isoCounts={isoCounts} isoHighRisk={isoHighRisk} scale={shadeScale} />
      </MapContainer>

      <Box
        sx={{
          position: "absolute",
          bottom: 20,
          left: 20,
          zIndex: 1000,
          background: `${colors.tooltipBg}db`,
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "12px",
          p: 1.5,
          minWidth: 180,
          boxShadow: SHADOW_LG,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            fontFamily: F_SANS,
            color: colors.tooltipText,
            mb: 1,
            opacity: 0.7,
            textTransform: "uppercase",
            letterSpacing: "0.6px",
          }}
        >
          Screened by Origin
        </Typography>
        {shadeScale.length === 0 ? (
          <Typography sx={{ fontSize: 10, fontFamily: F_SANS, color: colors.tooltipTextMuted }}>No data yet</Typography>
        ) : (
          shadeScale.map((bin) => (
            <Box key={bin.label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
              <Box sx={{ width: 12, height: 10, borderRadius: "2px", background: bin.color, border: "1px solid rgba(255,255,255,0.15)", flexShrink: 0 }} />
              <Typography sx={{ fontSize: 10, fontFamily: F_SANS, color: colors.tooltipTextMuted }}>
                {bin.label} <Box component="span" sx={{ opacity: 0.6 }}>({bin.quantile})</Box>
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default GeoDashboardMap;
